import { useContext, useEffect, useState } from 'react'
import { useRecoilState } from 'recoil'
import dayjs from 'dayjs'
import healthClaimFormAtom from '@/recoil/healthClaimForm/atom'
import { HealthClaimModeEnum } from '@/constant/E_CLAIM'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'
import { usePolicyCoverage } from '@/services/healthClaim/usePolicyCoverage'
import { DocumentDispatchContext } from '../HealthClaimDetailPage'
import { useSupportingDocumentsForClaim } from '@/hooks'
import { PolicyDetailResponse } from '@/services/healthClaim/useGetClaimDetail/types'

export const InsuredPersonDetailHook = () => {
  const [search, setSearch] = useState('')
  const [healthClaim, setHealthClaim] = useRecoilState(healthClaimFormAtom)
  const healthClaimForm = useHealthClaimForm()
  const {
    resetAllForms,
    selectedPolicy,
    setSelectedPolicyOverride,
    forms: { insuredPersonForm },
  } = healthClaimForm

  const isCreateMode = healthClaim.mode === HealthClaimModeEnum.CREATE
  const isCreateOrEditMode =
    healthClaim.mode === HealthClaimModeEnum.CREATE || healthClaim.mode === HealthClaimModeEnum.EDIT

  const { data: policyCoverages, isLoading: isPolicyCoveragesLoading } = usePolicyCoverage(search)
  const dispatch = useContext(DocumentDispatchContext)
  const { initialState: emptyDocuments } = useSupportingDocumentsForClaim()

  // Update health claim state when policy coverages are fetched
  useEffect(() => {
    if (policyCoverages?.length > 0) {
      const currentIdentity = healthClaim.data?.identityId || ''
      const isNewInsured = Boolean(search) && search !== currentIdentity

      // If user searched a different insured person, clear all forms and documents regardless of reclaim
      if (isNewInsured) {
        resetAllForms()
        try {
          dispatch({ type: 'INIT', payload: emptyDocuments })
        } catch {}
      } else if (!(healthClaim.reclaim === true)) {
        // In non-reclaim flow, still reset when fetching policies
        resetAllForms()
      }

      // Set to health claim state
      setHealthClaim((prev) => ({
        ...prev,
        // If switching to a different insured, break reclaim linkage and drop draft
        reclaim: isNewInsured ? false : prev.reclaim,
        data: {
          ...prev.data,
          identityId: search,
          policyDetails: policyCoverages.map(
            (c) =>
              ({
                insuredFirstName: c.firstName,
                insuredLastName: c.lastName,
                icHospitalAccident: c.icHospitalAccident,
                icHospitalAccidentName: c.icHospitalAccidentName,
                icHospitalAccidentAddress: c.icHospitalAccidentAddress,
                icHospitalSickness: c.icHospitalSickness,
                icMotorcyclePassenger: c.icMotorcyclePassenger,
                icSevereIllness: c.icSevereIllness,
                dateOfBirth: new Date(c.dateOfBirth),
                policyGroup: c.policyGroup,
                policyNumber: c.policyNumber,
                effectiveDate: new Date(c.effectiveDate),
                expiryDate: new Date(c.expiryDate),
                excludedDisease: c.excludedDiseases,
                ipdCoverage: c.ipdCoverage,
                opdCoverage: c.opdCoverage,
                paCoverage: c.paCoverage,
                isSelectClaim: false, // Default to false
              } as PolicyDetailResponse),
          ),
        },
        draft: isNewInsured ? undefined : (prev as any).draft,
        hasRequestedDocuments: isNewInsured ? false : prev.hasRequestedDocuments,
        pendingRequestDocuments: isNewInsured ? [] : prev.pendingRequestDocuments,
      }))

      setSelectedPolicyOverride(null)
    }
  }, [
    policyCoverages,
    search,
    setHealthClaim,
    resetAllForms,
    healthClaim.reclaim,
    healthClaim.data?.identityId,
    dispatch,
    emptyDocuments,
    setSelectedPolicyOverride,
  ])

  // Update form values when selected policy coverage changes
  useEffect(() => {
    if (selectedPolicy && healthClaim.data) {
      const formValues = {
        identityId: healthClaim.data?.identityId || '',
        firstName: selectedPolicy.insuredFirstName || '',
        lastName: selectedPolicy.insuredLastName || '',
        dob: selectedPolicy.dateOfBirth ? dayjs(selectedPolicy.dateOfBirth) : null,
        phoneNumber: healthClaim.data?.mobileNumber || '',
        email: healthClaim.data?.email || '',
      }

      insuredPersonForm.setFieldsValue(formValues)
    }
  }, [selectedPolicy, healthClaim.data, insuredPersonForm])

  return {
    // State
    search,
    setSearch,
    healthClaim,

    // Computed values
    isCreateMode,
    isCreateOrEditMode,
    selectedPolicyCoverage: selectedPolicy,
    isPolicyCoveragesLoading,

    // Form
    insuredPersonForm,
  }
}
